import { useAuth } from "@/hooks/useAuth"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { useRegisterSW } from "virtual:pwa-register/react"

type SwStatusProviderProps = {
  children: React.ReactNode
}

type SwStatusProviderState = {
  offlineReady: boolean
  updateAvailable: boolean
  updateServiceWorker: () => Promise<void>
  ignoreUpdate: boolean
  setIgnoreUpdate: (ignore: boolean) => void
  isUpdating: boolean
  isDownloading: boolean
  checkForUpdate: () => Promise<boolean>
}

const initialState: SwStatusProviderState = {
  offlineReady: false,
  updateAvailable: false,
  updateServiceWorker: () => Promise.resolve(),
  ignoreUpdate: false,
  setIgnoreUpdate: (_: boolean) => {},
  isUpdating: false,
  isDownloading: false,
  checkForUpdate: () => Promise.resolve(false),
}

const SwStatusProviderContext =
  createContext<SwStatusProviderState>(initialState)

const registerPeriodicSync = (
  period: number,
  swUrl: string,
  r: ServiceWorkerRegistration,
) => {
  if (period <= 0) return undefined

  return setInterval(async () => {
    if ("onLine" in navigator && !navigator.onLine) return

    const resp = await fetch(swUrl, {
      cache: "no-store",
      headers: {
        cache: "no-store",
        "cache-control": "no-cache",
      },
    })

    if (resp?.status === 200) await r.update()
  }, period)
}

export function SwStatusProvider({ children, ...props }: SwStatusProviderProps) {
  const { isLoggedIn } = useAuth()
  const [ignoreUpdate, setIgnoreUpdate] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const regRef = useRef<ServiceWorkerRegistration | undefined>(undefined)
  const swUrlRef = useRef<string | undefined>(undefined)
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  )

  const period = isLoggedIn ? 60 * 5 * 1000 : 0 // 5 minutes if logged in

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      regRef.current = r
      swUrlRef.current = swUrl
      if (period <= 0) return
      if (r?.active?.state === "activated") {
        intervalRef.current = registerPeriodicSync(period, swUrl, r)
      } else if (r?.installing) {
        r.installing.addEventListener("statechange", (e) => {
          const sw = e.target as ServiceWorker
          if (sw.state === "activated") {
            intervalRef.current = registerPeriodicSync(period, swUrl, r)
          }
        })
      }
    },
  })

  const updateAvailable = isLoggedIn && needRefresh

  useEffect(() => {
    return () => {
      if (intervalRef.current !== undefined) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const checkForUpdate = useCallback(async (): Promise<boolean> => {
    const registration = regRef.current
    if (!registration) {
      console.warn("No service worker registration available")
      return false
    }

    const url = swUrlRef.current ?? "/sw.js"
    const resp = await fetch(url, {
      cache: "no-store",
      headers: {
        cache: "no-store",
        "cache-control": "no-cache",
      },
    })

    if (resp?.status === 200) {
      console.log("update")
      await registration.update()

      // If a new SW is installing, wait for it to finish downloading
      const installing = registration.installing
      if (installing) {
        setIsDownloading(true)
        await new Promise<void>((resolve) => {
          installing.addEventListener("statechange", () => {
            if (
              installing.state === "installed" ||
              installing.state === "activated"
            ) {
              setIsDownloading(false)
              resolve()
            } else if (installing.state === "redundant") {
              setIsDownloading(false)
              resolve()
            }
          })
        })
        return true
      }

      return false
    }

    return false
  }, [])

  const wrappedUpdateServiceWorker = useCallback(async () => {
    setIsUpdating(true)
    // pause here to allow the updating state to be set, the app is non-responsive while updating
    await new Promise((resolve) => setTimeout(resolve, 500))
    await updateServiceWorker().catch(() => setIsUpdating(false))
  }, [updateServiceWorker])

  return (
    <SwStatusProviderContext.Provider
      {...props}
      value={{
        offlineReady: Boolean(navigator.serviceWorker.controller),
        ignoreUpdate,
        setIgnoreUpdate,
        updateAvailable,
        checkForUpdate,
        updateServiceWorker: wrappedUpdateServiceWorker,
        isUpdating,
        isDownloading,
      }}
    >
      {children}
    </SwStatusProviderContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useSwStatus = () => {
  const context = useContext(SwStatusProviderContext)

  if (context === undefined)
    throw new Error("useSwStatus must be used within a SwStatusProvider")

  return context
}
